/**
 * @documental
 * @export Field
 * @name
 * MultiSelectDropdown.Field
 */
import { FC, ReactNode, useCallback, useEffect, useMemo } from "react";
import { FieldValues, useController, useFormContext } from "react-hook-form";

import { VALIDATION } from "../../constants/validation";
import { FieldBaseProps } from "../FieldBaseProps";
import { useInputDisabled, useInputReadonly } from "../InputGroup";
import { SubmitGroup } from "../SubmitGroup";
import { ValidityGroup } from "../ValidityGroup";
import { Input } from "./Input";
import { MultiSelectDropdownProps } from "./MultiSelectDropdown";
import { AccessibilityProps } from "../../AccessibilityProps";

/**
 * The `Field` component properties.
 */
export interface FieldProps
  extends FieldBaseProps<ReadonlyArray<string | number> | null>,
    Pick<MultiSelectDropdownProps, "onSearchChange">, Pick<AccessibilityProps, "aria-label"> {
  /**
   * Content to show after the text.
   */
  readonly prefix?: ReactNode;

  /**
   * Content to show prior to the text.
   */
  readonly suffix?: ReactNode;

  /**
   * The options to display.
   */
  readonly children?: ReactNode;

  /**
   * The placeholder text.
   */
  readonly placeholder?: string;

  /**
   * Indicates whether the dropdown is able to be searched. If not specified defaults to `false`.
   */
  readonly searchable?: boolean;
}

/**
 * A React Hook Form compatible `MultiSelectDropdown` component. For more information, see the documentation for
 * the <a href="https://react-hook-form.com/api">React Hook Form API</a>.
 */
export const Field: FC<FieldProps> = (props) => {
  const { name, children, validator, required = false, defaultValue = null } = props;

  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);

  const validateHandler = useCallback(
    (value: readonly (string | number)[], formValues: FieldValues) => {
      if (required && (value === null || value === undefined || value.length === 0)) return VALIDATION.REQUIRED_AT_LEAST_ONE;

      return validator ? validator(value, formValues) : true;
    },
    [required, validator],
  );

  const rules = useMemo(
    () => ({
      validate: disabled || readOnly ? undefined : validateHandler,
    }),
    [validateHandler, disabled, readOnly],
  );

  const { resetField } = useFormContext();

  useEffect(() => {
    if (disabled) {
      resetField(name);
    }
  }, [disabled]);

  const { field, fieldState, formState } = useController({
    name,
    defaultValue,
    rules,
  });

  const { ref, value, onChange, onBlur } = field;
  const { invalid, error } = fieldState;
  const { isSubmitting } = formState;

  return (
    <SubmitGroup submitting={isSubmitting}>
      <ValidityGroup invalid={invalid} error={error?.message}>
        <Input {...props} ref={ref} value={value} onChange={onChange} onFocusExit={onBlur}>
          {children}
        </Input>
      </ValidityGroup>
    </SubmitGroup>
  );
};
